import React, { Component } from 'react';
import _ from 'lodash';

import LivePage from './LivePage';
import submenuTests from './menus/submenu-tests';
import { ImagePreview } from '../components/images/ImagePreview';
import ModelIdSelector from '../components/lego/ModelIdSelector';
import { stringToColor } from '../components/common/colorUtils';
import BadgeColorHash from '../components/common/BadgeColorHash';
import SingleValueSelector from '../components/common/editors/SingleValueSelector';
import {
  decodeEmbeddingBase64,
  cosineSimilarity,
  cosineToUnit,
  angularDistance,
  pca2dFromVectors,
  similarityColor,
  topNeighborsBySimilarity,
  nearestNeighborEdges,
} from '../components/embedding-explorer/embeddingExplorerUtils.js';
import ImagePCAScatterPlot from '../components/embedding-explorer/ImagePCAScatterPlot';
import { CompareImagePreview } from '../components/images/CompareImagePreview';
import SimilarityBadge from '../components/common/SimilarityBadge';
import BadgeId from '../components/common/BadgeId';
import { IconButton } from '../components/common/IconButton';
import ImageTopSimilarityFinder from '../components/images/ImageTopSimilarityFinder';
import ImageClusterPreview from '../components/images/ImageClusterPreview';

const sampleData = [];

function parseEmbeddingRow(row, idx) {
  const imgUrl = row.imgUrl || row.url;
  if (!imgUrl) {
    throw new Error(`Row ${idx}: missing imgUrl or url`);
  }
  let vector;
  if (typeof row.embedding === 'string') {
    vector = decodeEmbeddingBase64(row.embedding);
  } else if (Array.isArray(row.embedding)) {
    vector = row.embedding.map(Number);
  } else {
    throw new Error(`Row ${idx}: embedding must be base64 string or number array`);
  }
  if (!vector.length) {
    throw new Error(`Row ${idx}: empty embedding`);
  }
  return {
    imgId: row.imgId || row._id || null,
    imgUrl,
    vector,
    // Stored cluster membership (from the model-embeddings service); -1 if none.
    clusterIdx: Number.isInteger(row.clusterIdx) ? row.clusterIdx : -1,
    clusterId: row.clusterId || null,
  };
}

class TestEmbeddingPage extends LivePage {
  constructor(props) {
    super(props);

    this.fullScreen = true;

    this.submenu = submenuTests;

    this.modelEmbSvc = this.service('services/data/fusebox-model-embeddings');
    const initialModelId = this.getUrlParam('modelId') || '';

    this.state = {
      ...this.state,
      modelId: initialModelId,
      setup: this.getUrlParam('setup') || '',
      setups: [],
      loadingModel: false,
      loadInfo: null,
      allItems: [],            // full parsed dataset (before cluster filter)
      items: [],               // active view (allItems, optionally filtered by cluster)
      clusters: [],            // precomputed [{ clusterId, clusterIdx, count, members }] (count desc)
      clustersById: {},        // clusterId -> cluster entry (O(1) lookup)
      clusterFilter: null,     // selected clusterId to filter by, or null = all
      parseError: null,
      fileName: null,
      selectedImgId: null,
      scatterHoverIdx: null,
      pcaCoords: null,
      pcaVariance: null,
      nnEdges: [],
      showNnEdges: true,
      imageUrlFilter: '',
    };

    setTimeout(() => {
      this.modelEmbSvc.find({ query: { listSetups: true } })
        .then(res => this.setState({ setups: res?.setups || [] }))
        .catch(err => console.warn('Failed to list embedding setups', err));
      // If a modelId is present in the url, load its embeddings; otherwise show the sample.
      if (this.state.modelId) this.loadModelEmbeddings();
      else this.applyParsedJson(sampleData, 'Sample');
    }, 100)
  }

  /**
   * Loads every stored embedding for the selected modelId from the
   * fusebox-model-embeddings service and feeds them into the explorer.
   */
  async loadModelEmbeddings() {
    const modelId = (this.state.modelId || '').trim();
    if (!modelId) return;
    const setup = this.state.setup || '';

    this.setUrlParams({ modelId, setup });
    this.setState({ loadingModel: true, parseError: null, loadInfo: null });
    try {
      const data = await this.modelEmbSvc.find({ query: { modelId, setup } });
      const items = data?.items || [];
      if (!items.length) {
        this.setState({
          loadingModel: false,
          fileName: `model:${modelId}`,
          parseError: `No embeddings found for model "${modelId}" `
            + `(clusters: ${data?.totalClusters || 0}, images: ${data?.totalImages || 0}).`,
        });
        return;
      }
      // items are { imgId, imgUrl, embedding: number[] } — the shape applyParsedJson expects.
      this.applyParsedJson(items, `model:${modelId}`);
      this.setState({ loadingModel: false, loadInfo: data });
    } catch (err) {
      console.error(err);
      this.setState({ loadingModel: false, parseError: err.toString() });
    }
  }

  applyParsedJson(data, fileName) {
    if (!Array.isArray(data)) {
      this.setState({ parseError: 'JSON root must be an array of { imgUrl, embedding }', fileName });
      return;
    }
    const items = [];
    for (let i = 0; i < data.length; i++) {
      try {
        items.push(parseEmbeddingRow(data[i], i));
      } catch (err) {
        this.setState({ parseError: err.message, fileName });
        return;
      }
    }
    const clusters = this.buildClusterIndex(items);
    const clustersById = _.keyBy(clusters, 'clusterId');
    this.markCrossClusterNeighbors(items, clustersById);
    this.setState({
      allItems: items,
      clusters,
      clustersById,
      clusterFilter: null,
      selectedImgId: null,
      scatterHoverIdx: null,
      parseError: null,
      fileName,
      ...this.viewStateForItems(items),
    });
  }

  /**
   * Groups the dataset by stored cluster once at load time, so cluster count,
   * members and per-cluster lookups don't have to be recomputed on every render or
   * filter. Returns [{ clusterId, clusterIdx, count, members }] sorted by count desc.
   * `members` are item references in dataset order (matches a plain filter).
   */
  buildClusterIndex(items) {
    const byId = new Map();
    items.forEach(it => {
      if (!it.clusterId) return;
      let entry = byId.get(it.clusterId);
      if (!entry) {
        entry = { clusterId: it.clusterId, clusterIdx: it.clusterIdx, count: 0, members: [] };
        byId.set(it.clusterId, entry);
      }
      entry.members.push(it);
      entry.count++;
    });
    return _.orderBy([...byId.values()], 'count', 'desc');
  }

  /**
   * Precomputes, over the FULL dataset, whether each item's globally-closest image
   * lives in a different stored cluster (own cluster must have ≥2 members). The
   * result is stamped onto each item as `crossCluster`, so the scatter's yellow
   * border survives cluster filtering — the mark stays even when the closest image
   * itself is hidden by the active cluster filter. Mutates items in place.
   */
  markCrossClusterNeighbors(items, clustersById) {
    const edges = nearestNeighborEdges(items);
    items.forEach(it => { it.crossCluster = false; });
    edges.forEach(e => {
      const a = items[e.from];
      const b = items[e.to];
      a.crossCluster = !!(a.clusterId && b && b.clusterId
        && a.clusterId !== b.clusterId
        && (clustersById[a.clusterId]?.count || 0) >= 2);
    });
  }

  /**
   * Builds the scatter "view" for a set of items: PCA 2D coords, nearest-neighbor
   * edges, and a clean selection reset. Shared by the initial load and
   * the cluster filter, so the visualization is recomputed over whatever subset is
   * currently active. Points are colored by their stored cluster id (renderScatter).
   */
  viewStateForItems(items) {
    const { coords, varianceExplained } = pca2dFromVectors(items.map(it => it.vector));
    return {
      items,
      pcaCoords: coords,
      pcaVariance: varianceExplained,
      nnEdges: nearestNeighborEdges(items),
      imageUrlFilter: '',
      scatterHoverIdx: null,
    };
  }

  /**
   * Filters the full dataset down to a single stored cluster (or all when cleared)
   * and recomputes the PCA visualization over just that subset.
   */
  applyClusterFilter = (clusterId) => {
    const { allItems, clustersById } = this.state;
    const items = clusterId ? (clustersById[clusterId]?.members || []) : allItems;
    this.setState({
      clusterFilter: clusterId || null,
      ...this.viewStateForItems(items),
    });
  };

  renderScatter() {
    const { pcaCoords, selectedImgId, items, nnEdges, showNnEdges, clustersById } = this.state;
    if (!pcaCoords || !pcaCoords.length) return null;

    // Resolve the selected image to an index into the active view (-1 if it's been
    // filtered out / not present); imgId keeps the selection stable across filtering.
    const selIdx = selectedImgId == null ? -1 : items.findIndex(it => it.imgId === selectedImgId);
    const selViewIdx = selIdx >= 0 ? selIdx : null;

    const neighborByIdx = topNeighborsBySimilarity(items, selViewIdx, 10);

    // Points are colored by their stored cluster id. PcaScatter handles the
    // selected/neighbor coloring.
    const baseColorForItem = (item) => (item.clusterId ? stringToColor(item.clusterId, { white: 0.5 }) : '#6c757d');
    // A single-member stored cluster is drawn as a square.
    const shapeForItem = (item) => (clustersById[item.clusterId]?.count === 1 ? 'rect' : 'circle');

    return (
      <ImagePCAScatterPlot
        items={items}
        coords={pcaCoords}
        height={460}
        nnEdges={nnEdges}
        showNnEdges={showNnEdges}
        onToggleNnEdges={(v) => this.setState({ showNnEdges: v })}
        selectedIndex={selViewIdx}
        neighborByIndex={neighborByIdx}
        baseColorForItem={baseColorForItem}
        shapeForItem={shapeForItem}
        crossClusterBorder
        onPointClick={(item) => this.setState({ selectedImgId: item.imgId })}
        onBackgroundClick={() => this.setState({ selectedImgId: null, scatterHoverIdx: null })}
      />
    );
  }

  renderSimilarityPanel() {
    const { allItems, selectedImgId } = this.state;
    const sel = selectedImgId == null ? null : allItems.find(it => it.imgId === selectedImgId);

    if (!sel) {
      return (
        <div className="alert alert-secondary">
          Click an image in the grid (or a point in the scatter plot) to sort all others by cosine similarity and show
          distance metrics.
        </div>
      );
    }

    const rows = allItems
      .map((it, idx) => ({ idx, it }))
      .filter(r => r.it.imgId !== selectedImgId)
      .map(r => {
        const sim = cosineSimilarity(sel.vector, r.it.vector);
        return {
          idx: r.idx,
          imgId: r.it.imgId,
          imgUrl: r.it.imgUrl,
          clusterId: r.it.clusterId,
          similarity: cosineToUnit(sim),
          angularDist: angularDistance(sel.vector, r.it.vector),
        };
      })
      .sort((a, b) => b.similarity - a.similarity).slice(0, 50);

    return (
      <div>
        <div className="d-flex align-items-start flex-wrap test-embedding-selected-header">
          <ImagePreview
            url={sel.imgUrl}
            zoom
            img200
            contain
            className="test-embedding-selected-header__thumb mr-3 mb-1"
            title={sel.imgUrl}
          />

          <div>
            <div>
              <span className="font-weight-bold mr-2">
                Image #{allItems.indexOf(sel) + 1}
              </span>
              <IconButton level="violeta" icon="image_search" onClick={() => this.openSimilarImagesModal(sel.imgId)} />

              {sel.clusterId && (
                <span className='ml-3'>
                  Cluster: <BadgeColorHash id={sel.clusterId} className="user-select-all mr-2" />

                  {this.state.clusterFilter !== sel.clusterId && <IconButton level="primary" icon="filter_list" onClick={() => this.applyClusterFilter(sel.clusterId)} />}
                </span>
              )}
            </div>


            <ImageClusterPreview clusterId={sel.clusterId} size="50" maxImages={10} />
          </div>
        </div>

        <div className="table-responsive similarity-table">
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>Thumb</th>
                <th>Image / Cluster</th>
                <th>Similarity</th>
                <th className="text-truncate" style={{ maxWidth: '240px' }}>
                  URL
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rank = i + 1;
                const inTopTen = rank <= 10;
                const badgeColor = inTopTen ? similarityColor(r.similarity) : null;
                return (
                  <tr key={r.idx}>
                    <td>
                      {inTopTen ? (
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 22,
                            padding: '0 6px',
                            borderRadius: 11,
                            background: badgeColor,
                            color: '#111',
                            fontWeight: 'bold',
                            textAlign: 'center',
                          }}
                          title={`Similarity [0,1]: ${r.similarity.toFixed(4)}`}
                        >
                          {rank}
                        </span>
                      ) : (
                        rank
                      )}
                    </td>
                    <td>
                      <CompareImagePreview
                        left
                        compareUrl={sel.imgUrl}
                        url={r.imgUrl}
                        zoom
                        img100
                        contain
                        onImageClick={() => this.setState({ selectedImgId: r.imgId })}
                      />
                    </td>
                    <td className="small">
                      {r.imgId
                        ? <code className="user-select-all" title="Image id (click to select all, then copy)">{r.imgId}</code>
                        : <span className="text-muted">—</span>}
                      <br />Cluster: <BadgeColorHash id={r.clusterId} className="user-select-all" />
                    </td>
                    <td><SimilarityBadge value={r.similarity} /></td>
                    <td className="small text-break">
                      <a href={r.imgUrl} target="_blank" rel="noreferrer">
                        {r.imgUrl}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  openSimilarImagesModal(imgId) {
    this.openModal(<ImageTopSimilarityFinder imgId={imgId} showControls={true} />, {
      title: `Global images most similar to ` + imgId, fullScreenFixed: true, noPadding: true
    });
  }

  renderPageBody() {
    const {
      modelId,
      loadingModel,
      loadInfo,
      allItems,
      items,
      clusters,
      clusterFilter,
      parseError,
      fileName,
      selectedImgId,
      pcaVariance,
      imageUrlFilter,
    } = this.state;

    // One option per stored cluster (precomputed at load), colorized with the same
    // hash as the scatter points. Already sorted by descending member count.
    const clusterOptions = clusters.map(c => ({
      value: c.clusterId,
      count: c.count,
      label: (
        <span className="d-inline-flex align-items-center zoom-75" style={{ gap: '6px' }}>
          <svg width="14" height="14" style={{ marginRight: 2 }}>
            <circle
              cx="7"
              cy="7"
              r="6"
              fill={stringToColor(c.clusterId)}
              stroke="#ccc"
              strokeWidth="1"
            />
          </svg>
          Cluster <BadgeColorHash id={c.clusterId} />
          <span className="small text-muted">({c.count})</span>
        </span>
      ),
    }));

    const urlFilter = imageUrlFilter.trim().toLowerCase();
    const filteredGridItems = (urlFilter ? items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => (it.imgUrl || '').toLowerCase().includes(urlFilter))
      : items.map((it, idx) => ({ it, idx }))).slice(0, 200);

    return (
      <div className="test-embedding-page margin p-3">
        <div className="d-flex align-items-center flex-wrap mb-3 gap-2">
          <strong className="mr-2">Fusebox embedding by model:</strong>
          <ModelIdSelector
            value={modelId}
            onChange={id => this.setState({ modelId: id || '' })}
          />
          <select
            className="form-control form-control-sm d-inline-block ml-2"
            style={{ width: '280px' }}
            value={this.state.setup}
            onChange={e => this.setState({ setup: e.target.value })}
            title="Embedding setup (setupName in images_embed)"
          >
            <option value="">(default setup)</option>
            {this.state.setups.map(s => (
              <option key={s.setupName} value={s.setupName}>
                {s.setupName || '(unnamed)'} — {s.count} imgs
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary btn-sm ml-2"
            disabled={!modelId || loadingModel}
            onClick={() => this.loadModelEmbeddings()}
          >
            {loadingModel ? 'Loading…' : 'Load embeddings'}
          </button>
          {loadInfo && (
            <span className="small text-muted ml-2">
              {loadInfo.withEmbeddings} embeddings · {loadInfo.totalClusters} clusters · {loadInfo.totalImages} images
              {loadInfo.setup ? <> · setup <code>{loadInfo.setup}</code></> : null}
              {loadInfo.clusterType ? <> · type <code>{loadInfo.clusterType}</code></> : null}
            </span>
          )}
        </div>

        {parseError && (
          <div className="alert alert-danger">
            <strong>{fileName || 'File'}:</strong> {parseError}
          </div>
        )}

        {!!items.length && (
          <>
            <div className="row test-embedding-split mt-3">
              <div className="col-lg-6 test-embedding-split__col test-embedding-split__images">
                <div className="d-flex flex-wrap align-items-center mb-2">
                  {loadInfo && loadInfo.totalClusters ? (
                    <span className="text-muted small mr-3">
                      Points colored by stored cluster ({loadInfo.totalClusters})
                    </span>
                  ) : null}
                  {clusterOptions.length > 0 && (
                    <div className="d-inline-block mr-3" style={{ minWidth: '240px' }}>
                      <SingleValueSelector
                        value={clusterFilter}
                        options={clusterOptions}
                        placeholder="Filter by cluster…"
                        onChange={cid => this.applyClusterFilter(cid || null)}
                      />
                    </div>
                  )}
                  {clusterFilter && (
                    <span className="text-muted small mr-3">
                      {items.length} of {allItems.length} images
                    </span>
                  )}
                </div>

                {this.renderScatter()}

                <div className="d-flex align-items-center flex-wrap mb-2 gap-2">
                  <h5 className="mb-0">Images</h5>
                  <input
                    type="search"
                    className="form-control form-control-sm test-embedding-url-filter"
                    placeholder="Filter by URL…"
                    aria-label="Filter images by URL substring"
                    value={imageUrlFilter}
                    onChange={e => this.setState({ imageUrlFilter: e.target.value })}
                  />
                  {urlFilter ? (
                    <span className="small text-muted">
                      {filteredGridItems.length} of {items.length}
                    </span>
                  ) : null}
                </div>
                <div className="embedding-grid bg-dark">
                  {filteredGridItems.map(({ it, idx }) => (
                    <ImagePreview
                      key={idx}
                      url={it.imgUrl}
                      zoom
                      contain
                      selected={selectedImgId === it.imgId}
                      className="embedding-thumb"
                      title={it.imgUrl}
                      img200
                      onImageClick={() => this.setState({ selectedImgId: it.imgId })}
                      onError={e => {
                        e.target.style.opacity = 0.3;
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="col-lg-6 test-embedding-split__col test-embedding-split__table">
                <h5 className="mb-2">
                  Most similar images to: {selectedImgId ? <>
                    <BadgeId id={selectedImgId} /> <IconButton level="danger" icon="close" className='ml-2' onClick={() => this.setState({ selectedImgId: null })} /></> : '<select an image>'}
                </h5>
                {this.renderSimilarityPanel()}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default function TestEmbeddingPageComponent(props) {
  return <TestEmbeddingPage {...props} />;
}